/**
 * V5 — Post-generation validator for %%REPO_EDIT%% blocks.
 */

import type { AIEditBlock, AIFileEdit, AttachedRepoRef } from '@/services/repoEditService';
import { resolveEditQualifiedPath } from '@/services/repoEditService';

export interface RepoEditValidationResult {
  valid: boolean;
  blockedFiles: string[];
  reason?: string;
}

function pathInSet(path: string, set: Set<string>): boolean {
  if (set.has(path)) return true;
  // Qualified context may use owner/repo/path while single-repo edits use relative paths
  for (const entry of set) {
    if (entry.endsWith(`/${path}`) || entry === path) return true;
  }
  return false;
}

function validateFile(
  file: AIFileEdit,
  opts: {
    fullSet: Set<string>;
    treeSet: Set<string>;
    attachedRepos?: AttachedRepoRef[];
  }
): string | null {
  const path = file.file_path?.trim();
  if (!path || !file.content?.trim()) {
    return path || '(empty path)';
  }

  const multiRepo = (opts.attachedRepos?.length ?? 0) > 1;
  if (multiRepo && !file.repo?.trim()) {
    return path;
  }

  const qualified = resolveEditQualifiedPath(file, opts.attachedRepos ?? []);
  const pathsToCheck = [path, qualified].filter((p): p is string => Boolean(p));

  if (opts.treeSet.size > 0) {
    const inTree = pathsToCheck.some((p) => pathInSet(p, opts.treeSet));
    if (!inTree) return path;
  }

  if (opts.fullSet.size > 0) {
    const inFull = pathsToCheck.some((p) => pathInSet(p, opts.fullSet));
    if (!inFull) return path;
  }

  return null;
}

export function validateRepoEdit(
  editBlock: AIEditBlock,
  opts: {
    fullFilePaths: string[];
    repoTreePaths: string[];
    attachedRepos?: AttachedRepoRef[];
  }
): RepoEditValidationResult {
  const fullSet = new Set(opts.fullFilePaths);
  const treeSet = new Set(opts.repoTreePaths);
  const blockedFiles: string[] = [];

  if ((opts.attachedRepos?.length ?? 0) > 1) {
    for (const file of editBlock.files) {
      if (!file.repo?.trim()) {
        blockedFiles.push(file.file_path || '(missing repo)');
      }
    }
    if (blockedFiles.length > 0) {
      return {
        valid: false,
        blockedFiles,
        reason: 'Edit blocked — each file must include a "repo" field (owner/name) when multiple repositories are attached.',
      };
    }
  }

  for (const file of editBlock.files) {
    const blocked = validateFile(file, { fullSet, treeSet, attachedRepos: opts.attachedRepos });
    if (blocked) blockedFiles.push(blocked);
  }

  if (blockedFiles.length > 0) {
    return {
      valid: false,
      blockedFiles,
      reason: 'Edit blocked — target file was not fully loaded in FULL FILE CONTEXT for this turn.',
    };
  }

  return { valid: true, blockedFiles: [] };
}

export function filterValidEdits(
  files: AIFileEdit[],
  opts: {
    fullFilePaths: string[];
    repoTreePaths: string[];
    attachedRepos?: AttachedRepoRef[];
  }
): { validFiles: AIFileEdit[]; blockedFiles: string[] } {
  const fullSet = new Set(opts.fullFilePaths);
  const treeSet = new Set(opts.repoTreePaths);
  const validFiles: AIFileEdit[] = [];
  const blockedFiles: string[] = [];

  for (const file of files) {
    const blocked = validateFile(file, { fullSet, treeSet, attachedRepos: opts.attachedRepos });
    if (blocked) {
      blockedFiles.push(blocked);
      continue;
    }
    validFiles.push(file);
  }

  return { validFiles, blockedFiles };
}
